'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import Reveal from '../Reveal'

const COINS = [
  { sym: 'BTC', name: 'Bitcoin', price: 68000, ltv: 0.7 },
  { sym: 'ETH', name: 'Ethereum', price: 3500, ltv: 0.7 },
  { sym: 'SOL', name: 'Solana', price: 150, ltv: 0.6 },
  { sym: 'BNB', name: 'BNB', price: 600, ltv: 0.55 },
]

const TERMS = [
  { months: 6, apr: 0.009 },
  { months: 12, apr: 0.012 },
  { months: 24, apr: 0.019 },
  { months: 36, apr: 0.029 },
]

function fmt(n: number) {
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

export default function Calculator() {
  const [coinIdx, setCoinIdx] = useState(0)
  const [amount, setAmount] = useState(1) // units of collateral
  const [ltvPct, setLtvPct] = useState(50) // chosen loan-to-value
  const [termIdx, setTermIdx] = useState(1)

  const coin = COINS[coinIdx]
  const term = TERMS[termIdx]
  const maxLtv = Math.round(coin.ltv * 100)

  const collateralValue = coin.price * amount
  const effectiveLtv = Math.min(ltvPct, maxLtv)
  const loan = collateralValue * (effectiveLtv / 100)
  const monthlyInterest = (loan * term.apr) / 12
  const totalInterest = monthlyInterest * term.months

  const liquidationPrice = useMemo(() => {
    // liquidation when collateral value drops so LTV hits ~85%
    if (loan <= 0) return 0
    return (loan / 0.85) / amount
  }, [loan, amount])

  return (
    <section id="calculator" className="relative py-24">
      <div className="absolute inset-0 bg-radial-glow opacity-60 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
        <Reveal className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs tracking-[0.3em] text-electric-400 uppercase mb-3">
            Loan estimator
          </p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-chrome">
            See what you can borrow
          </h2>
          <p className="mt-4 text-steel-300">
            Adjust your collateral and terms to preview your credit line. Rates are
            illustrative — get an exact quote in seconds.
          </p>
        </Reveal>

        <Reveal>
          <div className="grid lg:grid-cols-5 gap-6 items-stretch">
            {/* Controls */}
            <div className="lg:col-span-3 glass rounded-3xl p-7 sm:p-9">
              {/* Coin select */}
              <label className="block text-sm text-steel-300 mb-3">Collateral asset</label>
              <div className="grid grid-cols-4 gap-2.5 mb-8">
                {COINS.map((c, i) => (
                  <button
                    key={c.sym}
                    onClick={() => {
                      setCoinIdx(i)
                      setLtvPct((p) => Math.min(p, Math.round(c.ltv * 100)))
                    }}
                    className={`py-3 rounded-xl text-sm font-semibold transition-all ${
                      i === coinIdx
                        ? 'bg-gradient-to-b from-electric-400 to-electric-600 text-white shadow-glow'
                        : 'glass text-steel-200 hover:text-white'
                    }`}
                  >
                    {c.sym}
                  </button>
                ))}
              </div>

              {/* Amount */}
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-steel-300">Collateral amount</label>
                <span className="text-sm font-semibold text-white">
                  {amount.toFixed(2)} {coin.sym}
                </span>
              </div>
              <input
                type="range"
                min={0.1}
                max={10}
                step={0.1}
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value))}
                className="w-full accent-electric-500 mb-2"
              />
              <div className="text-xs text-steel-400 mb-8">
                ≈ ${fmt(collateralValue)} at ${fmt(coin.price)}/{coin.sym}
              </div>

              {/* LTV */}
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-steel-300">Loan-to-value</label>
                <span className="text-sm font-semibold text-electric-300">
                  {effectiveLtv}% <span className="text-steel-500">/ max {maxLtv}%</span>
                </span>
              </div>
              <input
                type="range"
                min={10}
                max={maxLtv}
                step={5}
                value={effectiveLtv}
                onChange={(e) => setLtvPct(parseInt(e.target.value))}
                className="w-full accent-electric-500 mb-8"
              />

              {/* Term */}
              <label className="block text-sm text-steel-300 mb-3">Term & rate</label>
              <div className="grid grid-cols-4 gap-2.5">
                {TERMS.map((t, i) => (
                  <button
                    key={t.months}
                    onClick={() => setTermIdx(i)}
                    className={`py-3 rounded-xl text-center transition-all ${
                      i === termIdx
                        ? 'bg-gradient-to-b from-electric-400 to-electric-600 text-white shadow-glow'
                        : 'glass text-steel-200 hover:text-white'
                    }`}
                  >
                    <div className="text-sm font-semibold">{t.months}mo</div>
                    <div className="text-[11px] opacity-80">{(t.apr * 100).toFixed(1)}%</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-2 rounded-3xl p-7 sm:p-9 bg-gradient-to-b from-steel-800/80 to-ink-card ring-1 ring-electric-500/25 shadow-glow flex flex-col">
              <div className="text-sm text-steel-300">You can borrow up to</div>
              <div className="mt-1 font-display font-bold text-5xl text-glow-blue">
                ${fmt(loan)}
              </div>
              <div className="text-xs text-steel-400 mt-1">in USDC or fiat</div>

              <div className="neon-line my-7" />

              <dl className="space-y-4 text-sm">
                <div className="flex justify-between">
                  <dt className="text-steel-400">Monthly interest</dt>
                  <dd className="text-white font-medium">${fmt(monthlyInterest)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-steel-400">Total interest ({term.months}mo)</dt>
                  <dd className="text-white font-medium">${fmt(totalInterest)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-steel-400">APR</dt>
                  <dd className="text-electric-300 font-semibold">{(term.apr * 100).toFixed(1)}%</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-steel-400">Est. liquidation price</dt>
                  <dd className="text-amber-300 font-medium">${fmt(liquidationPrice)}</dd>
                </div>
              </dl>

              <Link
                href="/register"
                className="btn-shimmer mt-auto pt-6 block"
              >
                <span className="btn-shimmer inline-flex w-full items-center justify-center px-6 py-3.5 rounded-xl font-semibold bg-gradient-to-b from-electric-400 to-electric-600 text-white shadow-glow hover:shadow-glow-lg transition-shadow">
                  Claim this loan
                </span>
              </Link>
              <p className="text-[11px] text-steel-500 text-center mt-3">
                No credit check · No prepayment fees
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
